import React, { useCallback, useMemo, useRef } from 'react';
import {
  ClientDetail,
  FirstLine,
  RightDiv,
  Row,
  Tab,
  Title,
  RightContent,
  BottomContent,
  Row1,
  Empty,
  Link,
  LeftTitle,
  ActiveBtn,
  EmailTag,
  TextDiv
} from './styles';
import { isEmpty } from '../../../helpers/common';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  CUSTOMER_MEMBER_ROLE,
  CustomerDetailMoreMenu,
  EMPTY_STATE_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE,
  MENU_OPTIONS
} from '../../../global/constants';
import { MemberInputInterface } from '../../../interfaces/CustomerInterface';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import Tooltip from '../../../component/tooltip';
import IconButton from '../../../component/button/iconButton';
import { useMobileDevice } from '../../../global/useMobile';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import EmptyStateV2 from '../../../component/emptyState';

interface Props {
  openDeleteModal: () => void;
  setIsModalOpen: () => void;
  openEditModal: () => void;
  setSelectedItem: (item: MemberInputInterface) => void;
}

const CustomerDetailRight: React.FC<Props> = (props) => {
  const { openDeleteModal, setIsModalOpen, openEditModal, setSelectedItem } = props;

  //Refs
  const buttonRef = useRef<React.RefObject<HTMLDivElement>[]>([]);

  // mobx store variable
  const { workspaceStore, customerStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { individualCustomer, customerMembers } = customerStore;

  //Other variable
  const isMobile = useMobileDevice();

  // get current selected role icon
  const getCurrentSelectedRoleIcon = useCallback((role: number) => {
    switch (role) {
      case 1:
        return (
          <>
            <Tooltip title='No access' placement='bottomLeft' maxWidth={'max-content'}>
              <span>
                <SVGIcon
                  name='no-icon'
                  width={ICON_SIZE.MD}
                  height={ICON_SIZE.MD}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-secondary)'
                />
              </span>
            </Tooltip>
          </>
        );
      case 2:
        return (
          <>
            <Tooltip title='Project only' placement='bottomLeft' maxWidth={'max-content'}>
              <span>
                <SVGIcon
                  name='project-icon'
                  width={ICON_SIZE.MD}
                  height={ICON_SIZE.MD}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-secondary)'
                />
              </span>
            </Tooltip>
          </>
        );
      case 3:
        return (
          <>
            <Tooltip title='Full access' placement='bottomLeft' maxWidth={'max-content'}>
              <span>
                <SVGIcon
                  name='access-icon'
                  width={ICON_SIZE.MD}
                  height={ICON_SIZE.MD}
                  viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                  fill='var(--text-secondary)'
                />
              </span>
            </Tooltip>
          </>
        );
      default:
        return <></>;
    }
  }, []);

  // for opening delete model
  const OpenDeleteModel = useCallback(() => {
    openDeleteModal();
  }, [openDeleteModal]);

  // Toggles modal visibility.
  const handleOnClick = useCallback(() => {
    setIsModalOpen();
  }, [setIsModalOpen]);

  // open edit modal
  const onOpenEditModal = useCallback(() => {
    customerStore.setCustomerDetail(individualCustomer);
    openEditModal();
  }, [individualCustomer, customerStore, openEditModal]);

  // for delete member
  const onClickMemberDelete = useCallback(
    (item: MemberInputInterface) => {
      setSelectedItem(item);
      OpenDeleteModel();
    },
    [OpenDeleteModel, setSelectedItem]
  );

  // for edit member
  const onClickMemberEdit = useCallback(
    (member: MemberInputInterface) => {
      customerStore.setMemberInput(member);
      handleOnClick();
    },
    [customerStore, handleOnClick]
  );

  // handel member click
  const handleMemberClick = useCallback(
    (key: number | string, item: MemberInputInterface) => {
      const numericKey = Number(key);
      if (numericKey === MENU_OPTIONS.EDIT) {
        onClickMemberEdit(item);
      } else if (numericKey === MENU_OPTIONS.DELETE) {
        onClickMemberDelete(item);
      }
    },
    [onClickMemberDelete, onClickMemberEdit]
  );

  // render more menu items
  const renderMenuItems = useCallback(
    (customerMemberData: MemberInputInterface) => {
      return CustomerDetailMoreMenu.map((item) => (
        <DropdownItem
          key={item.key}
          label={item.label}
          onClick={() => handleMemberClick(item.key, customerMemberData)}
          tone={item.label === 'Delete' ? 'critical' : 'primary'}
        />
      ));
    },
    [handleMemberClick]
  );

  // render client details
  const renderClientDetails = useMemo(() => {
    if (customerMembers?.length > 0) {
      return customerMembers.map((item, index) => {
        let pendingUser;
        if (item?.role !== CUSTOMER_MEMBER_ROLE.NO_ACCESS) {
          pendingUser = workspace?.invitedUsers?.find((x) => x?.id === item?.id);
        }

        // Ensure the buttonRef for this index is created
        if (!buttonRef?.current[index]) {
          buttonRef.current[index] = React.createRef<HTMLDivElement>();
        }

        return (
          <Row key={item?.id}>
            <FirstLine>
              <LeftTitle>
                <p>{item?.name}</p>
                {getCurrentSelectedRoleIcon(item?.role)}
              </LeftTitle>
              <Tab>
                {pendingUser && (
                  <Tooltip
                    title='A member has been invited and is pending to join!'
                    placement='bottom'
                    maxWidth={'250px'}>
                    <ActiveBtn>Invited</ActiveBtn>
                  </Tooltip>
                )}
                <Dropdown
                  modalTitle='Manage Member'
                  activeClassName='active'
                  content={
                    <IconButton
                      iconName='three-dots-horizontal-icon'
                      size={isMobile ? ICON_BUTTON_SIZE.SMALL : ICON_BUTTON_SIZE.EXTRA_SMALL}
                      variant={ICON_BUTTON_TYPE.TERTIARY}
                    />
                  }
                  trigger='click'>
                  {renderMenuItems(item)}
                </Dropdown>
              </Tab>
            </FirstLine>
            <p>{item?.title}</p>
            <p>{item?.email}</p>
          </Row>
        );
      });
    }

    return (
      <Empty isMemberEmpty={true}>
        <EmptyStateV2
          title='No Members added yet'
          size={EMPTY_STATE_SIZE.SMALL}
          imageUrl={'customer_empty'}
          buttonTitle='Add Member'
          onClick={handleOnClick}
        />
      </Empty>
    );
  }, [customerMembers, handleOnClick, getCurrentSelectedRoleIcon, isMobile, renderMenuItems, workspace?.invitedUsers]);

  return (
    <>
      <RightDiv>
        <RightContent>
          <Title>
            <p>Member</p>
            {customerMembers?.length > 0 && (
              <Link role='button' onClick={handleOnClick}>
                + Add
              </Link>
            )}
          </Title>
          <ClientDetail>{renderClientDetails}</ClientDetail>
        </RightContent>
        <RightContent>
          <Title>
            <p>Additional Info</p>
            <Link role='button' onClick={onOpenEditModal}>
              Edit
            </Link>
          </Title>
          <BottomContent>
            <Row1>
              <FirstLine>
                <h5>Company Title</h5>
              </FirstLine>
              <TextDiv>{individualCustomer?.companyName}</TextDiv>
            </Row1>
            {!isEmpty(individualCustomer?.phone) && (
              <Row1>
                <FirstLine>
                  <h5>Phone Number</h5>
                </FirstLine>
                {!isEmpty(individualCustomer?.phone) ? <TextDiv>{individualCustomer?.phone}</TextDiv> : <></>}
              </Row1>
            )}
            {!isEmpty(individualCustomer?.email) && (
              <Row1>
                <FirstLine>
                  <h5>Email</h5>
                </FirstLine>
                <EmailTag>{individualCustomer?.email}</EmailTag>
              </Row1>
            )}
          </BottomContent>
        </RightContent>
      </RightDiv>
    </>
  );
};

export default observer(CustomerDetailRight);
